import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Container, Alert, Typography, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { GenericHeader } from 'components/GenericHeader';
import { CompanyDetails } from './CompanyDetails';
import { ConsentFormPanel } from './ConsentFormPanel';
import { Logger } from 'utils/Logger';

export const ConsentForm: React.FC = () => {
  const theme = useTheme();
  const clientConfig = useSelector((state: RootState) => state.clientConfig.data);
  const CONTACT_EMAIL = clientConfig?.contactEmail || 'sales@instantupdate.ai';
  const CONTACT_PHONE = clientConfig?.contactPhone || '+447837616656';
  const SUPPORT_EMAIL = clientConfig?.supportEmail || 'support@instantupdate.ai';
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('cid');
  const dataSetId = searchParams.get('dsid');
  const contactId = searchParams.get('rid');
  const [companyName, setCompanyName] = useState<string>('');
  const [companyError, setCompanyError] = useState<string>('');

  Logger.debug('ConsentForm.tsx', `ConsentForm rendered with params: cid=${companyId}, dsid=${dataSetId}, rid=${contactId}`);

  // Validate required parameters - only cid is mandatory
  if (!companyId || companyError) {
    return (
      <>
        <GenericHeader />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '1920px',
              mx: 'auto',
              px: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Card
              sx={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: 3,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgb(20, 25, 55)' 
                  : theme.palette.grey[100],
                border: `1px solid ${theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.12)' 
                  : theme.palette.grey[300]}`,
                boxShadow: theme.shadows[2],
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.primary }}>
                  You are not authorized to view this page.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Please contact{' '}
                  <Typography
                    component="a"
                    href={`mailto:${CONTACT_EMAIL}`}
                    sx={{
                      color: theme.palette.secondary.main,
                      textDecoration: 'none',
                      fontWeight: 'medium',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {CONTACT_EMAIL}
                  </Typography>
                  {' '}or{' '}
                  <Typography
                    component="a"
                    href={`tel:${CONTACT_PHONE}`}
                    sx={{
                      color: theme.palette.secondary.main,
                      textDecoration: 'none',
                      fontWeight: 'medium',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {CONTACT_PHONE}
                  </Typography>
                  {' '}for more information.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    If you believe this is an error, please contact our{' '}
                    <Typography
                      component="a"
                      href={`mailto:${SUPPORT_EMAIL}`}
                      sx={{
                        color: theme.palette.secondary.main,
                        textDecoration: 'none',
                        fontWeight: 'medium',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      support team
                    </Typography>
                    .
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <GenericHeader />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Company Details Panel */}
        <Box sx={{ mb: 4 }}>
          <CompanyDetails 
            companyId={companyId} 
            onCompanyDataLoaded={setCompanyName}
            onCompanyError={setCompanyError}
          />
        </Box>

        {/* Consent Form Panel - always show, with or without dsid/rid */}
        <Box sx={{ mb: 4 }}>
          <ConsentFormPanel 
            dataSetId={dataSetId || ''}
            contactId={contactId || ''}
            companyName={companyName}
            companyId={companyId}
          />
        </Box>
      </Container>
    </>
  );
}; 